import { createSlice } from "@reduxjs/toolkit";
import {
  getJDListAction,
  getJDByIdAction,
  createJDAction,
  updateJDAction,
  deleteJDAction,
  parseJDFileAction,
  uploadJDFileAction,
  generateJDAction,
  generateScreeningQuestionsAction,
  setSelectedJDAction
} from '../actions/jdsActions';

const jdsSlice = createSlice({
  name: "jds",
  initialState: {
    status: "idle",
    error: null,
    loading: false,
    jdLoading: false,
    jdError: null,
    // JD list and data
    jdList: [],
    currentJD: null,
    // JD operations
    uploadLoading: false,
    uploadError: null,
    parseLoading: false,
    parseError: null,
    generateLoading: false,
    generateError: null,
    selectedJD: null,
    // JD filters and pagination
    filters: {
      search: "",
      status: "",
      job_type: "",
      location: "",
      sort_by: "created_at",
      sort_order: "desc"
    },
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0
    },
    // JD statistics
    jdStats: {
      total: 0,
      active: 0,
      inactive: 0,
      draft: 0
    }
  },
  reducers: {
    setError: (state, action) => {
      state.error = action.payload;
      state.status = "failed";
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setJDLoading: (state, action) => {
      state.jdLoading = action.payload;
    },
    setJDError: (state, action) => {
      state.jdError = action.payload;
    },
    // JD list management
    setJDList: (state, action) => {
      state.jdList = action.payload;
    },
    setCurrentJD: (state, action) => {
      state.currentJD = action.payload;
    },
    setSelectedJD: (state, action) => {
      state.selectedJD = action.payload;
    },
    addJDToList: (state, action) => {
      state.jdList.unshift(action.payload);
    },
    updateJDInList: (state, action) => {
      const { jdId, updatedJD } = action.payload;
      state.jdList = state.jdList.map(jd => 
        jd.id === jdId || jd.jd_id === jdId ? { ...jd, ...updatedJD } : jd
      );
    },
    removeJDFromList: (state, action) => {
      const jdId = action.payload;
      state.jdList = state.jdList.filter(jd => 
        jd.id !== jdId && jd.jd_id !== jdId
      );
    },
    // Upload operations
    setUploadLoading: (state, action) => {
      state.uploadLoading = action.payload;
    },
    setUploadError: (state, action) => {
      state.uploadError = action.payload;
    },
    // Parse operations
    setParseLoading: (state, action) => {
      state.parseLoading = action.payload;
    },
    setParseError: (state, action) => {
      state.parseError = action.payload;
    },
    // Generate operations
    setGenerateLoading: (state, action) => {
      state.generateLoading = action.payload;
    },
    setGenerateError: (state, action) => {
      state.generateError = action.payload;
    },
    // Filters and pagination
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    // JD statistics
    setJDStats: (state, action) => {
      state.jdStats = action.payload;
    },
    // Clear JD state
    clearJDState: (state) => {
      state.status = "idle";
      state.error = null;
      state.loading = false;
      state.jdLoading = false;
      state.jdError = null;
      state.jdList = [];
      state.currentJD = null;
      state.uploadLoading = false;
      state.uploadError = null;
      state.parseLoading = false;
      state.parseError = null;
      state.generateLoading = false;
      state.generateError = null;
      state.selectedJD = null;
      state.filters = {
        search: "",
        status: "",
        job_type: "",
        location: "",
        sort_by: "created_at",
        sort_order: "desc"
      };
      state.pagination = {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
      };
      state.jdStats = {
        total: 0,
        active: 0,
        inactive: 0,
        draft: 0
      };
    }
  },
  extraReducers: (builder) => {
    // Get JD List
    builder
      .addCase(getJDListAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getJDListAction.fulfilled, (state, action) => {
        state.loading = false;
        state.jdList = action.payload;
        state.status = "succeeded";
      })
      .addCase(getJDListAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = "failed";
      });

    // Get JD By ID
    builder
      .addCase(getJDByIdAction.pending, (state) => {
        state.jdLoading = true;
        state.jdError = null;
      })
      .addCase(getJDByIdAction.fulfilled, (state, action) => {
        state.jdLoading = false;
        state.currentJD = action.payload;
      })
      .addCase(getJDByIdAction.rejected, (state, action) => {
        state.jdLoading = false;
        state.jdError = action.payload;
      });

    // Create JD
    builder
      .addCase(createJDAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createJDAction.fulfilled, (state, action) => {
        state.loading = false;
        state.jdList.unshift(action.payload);
      })
      .addCase(createJDAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update JD
    builder
      .addCase(updateJDAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateJDAction.fulfilled, (state, action) => {
        state.loading = false;
        const { jdId, data } = action.payload;
        state.jdList = state.jdList.map(jd => 
          jd.id === jdId || jd.jd_id === jdId ? { ...jd, ...data } : jd
        );
        if (state.currentJD && (state.currentJD.id === jdId || state.currentJD.jd_id === jdId)) {
          state.currentJD = { ...state.currentJD, ...data };
        }
      })
      .addCase(updateJDAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Delete JD
    builder
      .addCase(deleteJDAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteJDAction.fulfilled, (state, action) => {
        state.loading = false;
        const jdId = action.payload;
        state.jdList = state.jdList.filter(jd => 
          jd.id !== jdId && jd.jd_id !== jdId
        );
        if (state.currentJD && (state.currentJD.id === jdId || state.currentJD.jd_id === jdId)) {
          state.currentJD = null;
        }
      })
      .addCase(deleteJDAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Parse JD File
    builder
      .addCase(parseJDFileAction.pending, (state) => {
        state.parseLoading = true;
        state.parseError = null;
      })
      .addCase(parseJDFileAction.fulfilled, (state, action) => {
        state.parseLoading = false;
        // Parse result is handled in the component
      })
      .addCase(parseJDFileAction.rejected, (state, action) => {
        state.parseLoading = false;
        state.parseError = action.payload;
      });

    // Upload JD File
    builder
      .addCase(uploadJDFileAction.pending, (state) => {
        state.uploadLoading = true;
        state.uploadError = null;
      })
      .addCase(uploadJDFileAction.fulfilled, (state, action) => {
        state.uploadLoading = false;
        // Upload result is handled in the component
      })
      .addCase(uploadJDFileAction.rejected, (state, action) => {
        state.uploadLoading = false;
        state.uploadError = action.payload;
      });

    // Generate JD
    builder
      .addCase(generateJDAction.pending, (state) => {
        state.generateLoading = true;
        state.generateError = null;
      })
      .addCase(generateJDAction.fulfilled, (state, action) => {
        state.generateLoading = false;
        // Generate result is handled in the component
      })
      .addCase(generateJDAction.rejected, (state, action) => {
        state.generateLoading = false;
        state.generateError = action.payload;
      });

    // Generate Screening Questions
    builder
      .addCase(generateScreeningQuestionsAction.pending, (state) => {
        state.generateLoading = true;
        state.generateError = null;
      })
      .addCase(generateScreeningQuestionsAction.fulfilled, (state, action) => {
        state.generateLoading = false;
        // Generate result is handled in the component
      })
      .addCase(generateScreeningQuestionsAction.rejected, (state, action) => {
        state.generateLoading = false;
        state.generateError = action.payload;
      });

    // Set Selected JD
    builder
      .addCase(setSelectedJDAction.fulfilled, (state, action) => {
        state.selectedJD = action.payload;
      });
  },
});

export const {
  setError,
  setLoading,
  setJDLoading,
  setJDError,
  setJDList,
  setCurrentJD,
  addJDToList,
  updateJDInList,
  removeJDFromList,
  setUploadLoading,
  setUploadError,
  setParseLoading,
  setParseError,
  setGenerateLoading,
  setGenerateError,
  setFilters,
  setPagination,
  setJDStats,
  clearJDState,
  setSelectedJD   
} = jdsSlice.actions;

// Selectors
export const selectJDLoading = (state) => state.jds.loading;
export const selectJDError = (state) => state.jds.error;
export const selectJDList = (state) => state.jds.jdList;
export const selectCurrentJD = (state) => state.jds.currentJD;
export const selectJDLoadingState = (state) => state.jds.jdLoading;
export const selectJDErrorState = (state) => state.jds.jdError;
export const selectUploadLoading = (state) => state.jds.uploadLoading;
export const selectUploadError = (state) => state.jds.uploadError;
export const selectParseLoading = (state) => state.jds.parseLoading;
export const selectParseError = (state) => state.jds.parseError;
export const selectGenerateLoading = (state) => state.jds.generateLoading;
export const selectGenerateError = (state) => state.jds.generateError;
export const selectJDFilters = (state) => state.jds.filters;
export const selectJDPagination = (state) => state.jds.pagination;
export const selectJDStats = (state) => state.jds.jdStats;
export const selectSelectedJD = (state) => state.jds.selectedJD;

export default jdsSlice.reducer; 